import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  CircularProgress,
  Breadcrumbs,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Business as BusinessIcon,
  Devices as DevicesIcon,
  Dashboard as DashboardIcon,
  NavigateNext as NavigateNextIcon,
  Cancel as CancelIcon,
  Block as BlockIcon,
} from '@mui/icons-material';
import { useNotification } from '../../components/Notification';
import {
  fetchStatistics,
  selectStatistics,
  selectDashboardLoading,
  selectDashboardError,
  clearError,
} from './slice';

function Dashboard() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { showNotification } = useNotification();

  const statistics = useSelector(selectStatistics);
  const loading = useSelector(selectDashboardLoading);
  const error = useSelector(selectDashboardError);

  // Fetch statistics on mount
  useEffect(() => {
    dispatch(fetchStatistics());
  }, [dispatch]);

  // Show error notification
  useEffect(() => {
    if (error) {
      showNotification(error, 'error');
      dispatch(clearError());
    }
  }, [error, dispatch, showNotification]);

  // Statistics sections configuration
  const statisticsSections = [
    {
      sectionTitle: t('dashboard.sections.licenses'),
      sectionIcon: <DescriptionIcon sx={{ fontSize: 24, mr: 1 }} />,
      cards: [
        {
          title: t('dashboard.licenses.total'),
          value: statistics.countLicense,
          icon: <DescriptionIcon sx={{ fontSize: 40 }} />,
          color: '#2196f3',
          bgColor: '#e3f2fd',
        },
        {
          title: t('dashboard.licenses.approved'),
          value: statistics.countLicenseApproved || 0,
          icon: <CheckCircleIcon sx={{ fontSize: 40 }} />,
          color: (statistics.countLicenseApproved || 0) > 0 ? '#4caf50' : '#9e9e9e',
          bgColor: (statistics.countLicenseApproved || 0) > 0 ? '#e8f5e9' : '#f5f5f5',
        },
        {
          title: t('dashboard.licenses.waiting'),
          value: statistics.countLicenseWaiting,
          icon: <HourglassEmptyIcon sx={{ fontSize: 40 }} />,
          color: statistics.countLicenseWaiting > 0 ? '#ff9800' : '#9e9e9e',
          bgColor: statistics.countLicenseWaiting > 0 ? '#fff3e0' : '#f5f5f5',
        },
        {
          title: t('dashboard.licenses.rejected'),
          value: statistics.countLicenseRejected || 0,
          icon: <CancelIcon sx={{ fontSize: 40 }} />,
          color: (statistics.countLicenseRejected || 0) > 0 ? '#f44336' : '#9e9e9e',
          bgColor: (statistics.countLicenseRejected || 0) > 0 ? '#ffebee' : '#f5f5f5',
        },
        {
          title: t('dashboard.licenses.revoked'),
          value: statistics.countLicenseRevoked || 0,
          icon: <BlockIcon sx={{ fontSize: 40 }} />,
          color: '#9e9e9e',
          bgColor: '#f5f5f5',
        },
      ],
    },
    {
      sectionTitle: t('dashboard.sections.devices'),
      sectionIcon: <DevicesIcon sx={{ fontSize: 24, mr: 1 }} />,
      cards: [
        {
          title: t('dashboard.devices.total'),
          value: statistics.countDevice,
          icon: <DevicesIcon sx={{ fontSize: 40 }} />,
          color: '#00bcd4',
          bgColor: '#e0f7fa',
        },
        {
          title: t('dashboard.devices.approved'),
          value: statistics.countDeviceApproved || 0,
          icon: <CheckCircleIcon sx={{ fontSize: 40 }} />,
          color: (statistics.countDeviceApproved || 0) > 0 ? '#4caf50' : '#9e9e9e',
          bgColor: (statistics.countDeviceApproved || 0) > 0 ? '#e8f5e9' : '#f5f5f5',
        },
        {
          title: t('dashboard.devices.waiting'),
          value: statistics.countDeviceWaiting,
          icon: <HourglassEmptyIcon sx={{ fontSize: 40 }} />,
          color: statistics.countDeviceWaiting > 0 ? '#ff9800' : '#9e9e9e',
          bgColor: statistics.countDeviceWaiting > 0 ? '#fff3e0' : '#f5f5f5',
        },
        {
          title: t('dashboard.devices.rejected'),
          value: statistics.countDeviceRejected || 0,
          icon: <CancelIcon sx={{ fontSize: 40 }} />,
          color: (statistics.countDeviceRejected || 0) > 0 ? '#f44336' : '#9e9e9e',
          bgColor: (statistics.countDeviceRejected || 0) > 0 ? '#ffebee' : '#f5f5f5',
        },
        {
          title: t('dashboard.devices.revoked'),
          value: statistics.countDeviceRevoked || 0,
          icon: <BlockIcon sx={{ fontSize: 40 }} />,
          color: '#9e9e9e',
          bgColor: '#f5f5f5',
        },
      ],
    },
    {
      sectionTitle: t('dashboard.sections.units'),
      sectionIcon: <BusinessIcon sx={{ fontSize: 24, mr: 1 }} />,
      cards: [
        {
          title: t('dashboard.units.total'),
          value: statistics.countUnit,
          icon: <BusinessIcon sx={{ fontSize: 40 }} />,
          color: '#9c27b0',
          bgColor: '#f3e5f5',
        },
        {
          title: t('dashboard.units.active'),
          value: statistics.countUnitActived,
          icon: <CheckCircleIcon sx={{ fontSize: 40 }} />,
          color: statistics.countUnitActived > 0 ? '#4caf50' : '#9e9e9e',
          bgColor: statistics.countUnitActived > 0 ? '#e8f5e9' : '#f5f5f5',
        },
        {
          title: t('dashboard.units.waiting'),
          value: statistics.countUnitWaiting,
          icon: <HourglassEmptyIcon sx={{ fontSize: 40 }} />,
          color: statistics.countUnitWaiting > 0 ? '#ff9800' : '#9e9e9e',
          bgColor: statistics.countUnitWaiting > 0 ? '#fff3e0' : '#f5f5f5',
        },
      ],
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Header Section */}
      <Box sx={{ px: 3, pt: 3, pb: 2, backgroundColor: '#ffffff' }}>
        {/* Page Title */}
        <Typography variant="h5" component="h1" fontWeight="bold" sx={{ textAlign: 'left' }}>
          {t('dashboard.title')}
        </Typography>
      </Box>

      {/* Main Content */}
      <Paper elevation={0} sx={{ borderRadius: 0, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: '#ffffff', mt: 1 }}>
        <Box
          sx={{
            flex: 1,
            px: 3,
            pt: 1.5,
            pb: 2,
            backgroundColor: '#ffffff',
            overflow: 'auto',
            '&::-webkit-scrollbar': {
              display: 'none'
            },
            '-ms-overflow-style': 'none',
            'scrollbarWidth': 'none'
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {statisticsSections.map((section, sectionIndex) => (
                <Box key={sectionIndex}>
                  {/* Section Header */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    {section.sectionIcon}
                    <Typography variant="h6" fontWeight="600">
                      {section.sectionTitle}
                    </Typography>
                  </Box>

                  {/* Section Cards */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, width: '100%' }}>
                    {section.cards.map((card, cardIndex) => (
                      <Box
                        key={cardIndex}
                        sx={{
                          flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(33.333% - 11px)', lg: '1 1 calc(20% - 13px)' },
                          minWidth: { xs: '100%', sm: '250px', md: '200px' }
                        }}
                      >
                        <Card
                          elevation={2}
                          sx={{
                            height: '140px',
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: 4,
                            },
                          }}
                        >
                          <CardContent sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', '&:last-child': { pb: 2.5 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                  {card.title}
                                </Typography>
                                <Typography variant="h4" component="div" fontWeight="bold" sx={{ color: card.color, mt: 1 }}>
                                  {card.value}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  backgroundColor: card.bgColor,
                                  color: card.color,
                                  borderRadius: 2,
                                  p: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                {card.icon}
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
}

export default Dashboard;
